import { useQuery } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError } from "../../lib/errors";
import { MAX_PAGE_SIZE } from "../../lib/pagination";
import { queryKeys } from "../../lib/queryKeys";
import type { AlertEntityOption } from "./AlertEntitySelect";

type Agent = components["schemas"]["AgentResponse"];
type AgentVersion = components["schemas"]["AgentVersionResponse"];
type AlertChannel = components["schemas"]["AlertChannelResponse"];
type Batch = components["schemas"]["BatchResponse"];
type Marker = components["schemas"]["MarkerResponse"];
type Me = components["schemas"]["MeResponse"];
type Template = components["schemas"]["TemplateResponse"];

async function listAgents(): Promise<Agent[]> {
  const items: Agent[] = [];
  let pageToken: string | undefined;
  do {
    const { data, error } = await apiClient.GET("/v1/agents", {
      params: {
        query: { page_size: MAX_PAGE_SIZE, page_token: pageToken },
      },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    items.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return items;
}

async function listAgentVersions(): Promise<AgentVersion[]> {
  const items: AgentVersion[] = [];
  let pageToken: string | undefined;
  do {
    const { data, error } = await apiClient.GET("/v1/agent-versions", {
      params: {
        query: { page_size: MAX_PAGE_SIZE, page_token: pageToken },
      },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    items.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return items;
}

async function listMarkers(): Promise<Marker[]> {
  const items: Marker[] = [];
  let pageToken: string | undefined;
  do {
    const { data, error } = await apiClient.GET("/v1/markers", {
      params: {
        query: { page_size: MAX_PAGE_SIZE, page_token: pageToken },
      },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    items.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return items;
}

async function listBatches(): Promise<Batch[]> {
  const items: Batch[] = [];
  let pageToken: string | undefined;
  do {
    const { data, error } = await apiClient.GET("/v1/batches", {
      params: {
        query: { page_size: MAX_PAGE_SIZE, page_token: pageToken },
      },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    items.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return items;
}

async function listTemplates(): Promise<Template[]> {
  const items: Template[] = [];
  let pageToken: string | undefined;
  do {
    const { data, error } = await apiClient.GET("/v1/templates", {
      params: {
        query: { page_size: MAX_PAGE_SIZE, page_token: pageToken },
      },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    items.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return items;
}

async function listChannels(): Promise<AlertChannel[]> {
  const items: AlertChannel[] = [];
  let pageToken: string | undefined;
  do {
    const { data, error } = await apiClient.GET("/v1/alert-channels", {
      params: {
        query: { page_size: MAX_PAGE_SIZE, page_token: pageToken },
      },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    items.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return items;
}

async function getMe(): Promise<Me> {
  const { data, error } = await apiClient.GET("/v1/me");
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

function option(label: string, value: string): AlertEntityOption {
  return { label, value };
}

export interface AlertRuleOptions {
  agents: AlertEntityOption[];
  agentVersions: AlertEntityOption[];
  batches: AlertEntityOption[];
  channels: AlertChannel[];
  errorMessage?: string;
  isPending: boolean;
  markers: AlertEntityOption[];
  me?: Me;
  retry: () => void;
  templates: AlertEntityOption[];
}

export function useAlertRuleOptions(enabled = true): AlertRuleOptions {
  const agents = useQuery({
    enabled,
    queryKey: queryKeys.agents.list({ surface: "alert-rule-options" }),
    queryFn: listAgents,
  });
  const versions = useQuery({
    enabled,
    queryKey: queryKeys.agentVersions.list({
      surface: "alert-rule-options",
    }),
    queryFn: listAgentVersions,
  });
  const markers = useQuery({
    enabled,
    queryKey: queryKeys.markers.list({ surface: "alert-rule-options" }),
    queryFn: listMarkers,
  });
  const batches = useQuery({
    enabled,
    queryKey: queryKeys.batches.list({ surface: "alert-rule-options" }),
    queryFn: listBatches,
  });
  const templates = useQuery({
    enabled,
    queryKey: queryKeys.templates.list({ surface: "alert-rule-options" }),
    queryFn: listTemplates,
  });
  const channels = useQuery({
    enabled,
    queryKey: queryKeys.alertChannels.list({
      surface: "alert-rule-options",
    }),
    queryFn: listChannels,
  });
  const me = useQuery({
    enabled,
    queryKey: queryKeys.me,
    queryFn: getMe,
  });
  const queries = [agents, versions, markers, batches, templates, channels, me];
  const agentNames = new Map(
    (agents.data ?? []).map((agent) => [agent.id, agent.display_name]),
  );
  const error = queries.find((query) => query.isError)?.error;

  return {
    agents: (agents.data ?? []).map((agent) =>
      option(agent.display_name, agent.id),
    ),
    agentVersions: (versions.data ?? []).map((version) =>
      option(
        `${agentNames.get(version.agent_id) ?? "Agent"} · ${version.tag}`,
        version.id,
      ),
    ),
    batches: (batches.data ?? []).map((batch) =>
      option(batch.display_name ?? "Untitled batch", batch.id),
    ),
    channels: channels.data ?? [],
    errorMessage: error instanceof Error ? error.message : undefined,
    isPending: queries.some((query) => query.isPending),
    markers: (markers.data ?? []).map((marker) =>
      option(
        marker.current_revision.description ??
          marker.current_revision.display_name,
        marker.id,
      ),
    ),
    me: me.data,
    retry: () => {
      for (const query of queries) {
        void query.refetch();
      }
    },
    templates: (templates.data ?? []).map((template) =>
      option(template.display_name, template.id),
    ),
  };
}
